import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { failureMessage, isRatingTokenInvalid } from './problems';
import { SESSION_NAMESPACE } from './strings';

/**
 * §6's client-app `/r/:token` — five stars and a sentence, at 390 px, with no login (R13).
 *
 * The token **is** the credential and it names exactly one session, so there is nothing to
 * sign in to and nothing else this page can reach. It posts and then says thank you; it
 * never reads the session back, because a client who can rate a visit still has no
 * business seeing what was recorded during it.
 */
export const SCORES = [1, 2, 3, 4, 5] as const;

export function RatingPage({
  token,
  rate,
}: {
  token: string;
  rate: (token: string, score: number, text: string | null) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation(SESSION_NAMESPACE);
  // Zero is "no star yet", not a rating: R13's scale starts at one, so the submit button
  // has something to be disabled by without a nullable score the submit handler would
  // have to re-check after the button already refused the click.
  const [score, setScore] = useState(0);
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const message = isRatingTokenInvalid(error)
    ? t('rating.expired')
    : failureMessage(error, t('rating.expired'));

  if (sent) {
    return (
      <p role="status" data-testid="rating-thanks" className="p-4 text-lg">
        {t('rating.thanks')}
      </p>
    );
  }

  return (
    <form
      data-testid="rating-form"
      className="mx-auto max-w-sm p-4"
      onSubmit={(event) => {
        event.preventDefault();
        void rate(token, score, text === '' ? null : text).then(
          () => {
            setSent(true);
          },
          (failure: unknown) => {
            setError(failure);
          },
        );
      }}
    >
      <h1 className="mb-3 text-xl font-semibold">{t('rating.title')}</h1>
      <fieldset>
        <legend className="sr-only">{t('rating.score')}</legend>
        {SCORES.map((value) => (
          <button
            key={value}
            type="button"
            data-testid={`star-${value}`}
            aria-pressed={score === value}
            aria-label={t('rating.star', { count: value })}
            onClick={() => {
              setScore(value);
            }}
            className={`h-14 w-14 text-2xl ${value <= score ? 'text-copper' : 'text-muted-foreground'}`}
          >
            ★
          </button>
        ))}
      </fieldset>

      <label className="mt-3 block">
        {t('rating.text')}
        <textarea
          data-testid="rating-text"
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
          className="mt-1 block w-full rounded border border-border p-2"
        />
      </label>

      {message === null ? null : (
        <p role="alert" data-testid="rating-error" className="mt-2 text-destructive">
          {message}
        </p>
      )}

      <button
        type="submit"
        data-testid="rating-submit"
        disabled={score === 0}
        className="mt-3 w-full rounded bg-primary px-4 py-3 text-primary-foreground"
      >
        {t('rating.submit')}
      </button>
    </form>
  );
}
